import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

export interface WellViewFieldMapping {
    wvField: string;
    label: string;
    contentKey?: string;
    unitRBU?: string;
}

export interface WellViewSchemaMapping {
    uiName: string;
    fields: WellViewFieldMapping[];
    order: string[];
}

export interface WellViewMappings {
    [schemaKey: string]: WellViewSchemaMapping;
}

@Injectable({
    providedIn: 'root'
})
export class WellViewMappingService {
    private mappings$: Observable<WellViewMappings>;
    private mappingsCache: WellViewMappings | null = null;

    constructor(private http: HttpClient) {
        // Load mappings once and cache them
        this.mappings$ = this.http.get<WellViewMappings>('/assets/wv_mappings.json').pipe(
            map(data => {
                this.mappingsCache = data;
                return data;
            }),
            shareReplay(1)
        );
    }

    /**
     * Get all mappings
     */
    public getMappings(): Observable<WellViewMappings> {
        return this.mappings$;
    }

    /**
     * Get mapping for a specific WellView schema
     * @param schemaKey - The WellView schema key (e.g., 'wvWellHeader', 'wvWellboreSize')
     */
    public getSchemaMapping(schemaKey: string): Observable<WellViewSchemaMapping | null> {
        return this.mappings$.pipe(
            map(mappings => mappings[schemaKey] || null)
        );
    }

    /**
     * Get UI display name for a WellView schema
     * @param schemaKey - The WellView schema key
     */
    public getSchemaUIName(schemaKey: string): Observable<string> {
        return this.getSchemaMapping(schemaKey).pipe(
            map(mapping => mapping?.uiName || schemaKey)
        );
    }

    /**
     * Get field label for a specific field in a schema
     * @param schemaKey - The WellView schema key
     * @param fieldName - The field name (case-insensitive)
     */
    public getFieldLabel(schemaKey: string, fieldName: string): Observable<string> {
        return this.getSchemaMapping(schemaKey).pipe(
            map(mapping => {
                if (!mapping || !mapping.fields) {
                    return this.formatFieldName(fieldName);
                }
                
                // Find field mapping (case-insensitive)
                const field = mapping.fields.find(f => 
                    f.wvField.toLowerCase() === fieldName.toLowerCase()
                );
                
                return field?.label || this.formatFieldName(fieldName);
            })
        );
    }

    /**
     * Get display keys in correct order for a schema
     * @param schemaKey - The WellView schema key
     * @param availableKeys - Keys available in the data
     */
    public getOrderedDisplayKeys(schemaKey: string, availableKeys: string[]): Observable<string[]> {
        return this.getSchemaMapping(schemaKey).pipe(
            map(mapping => {
                if (!mapping || !mapping.order) {
                    return availableKeys;
                }
                
                // Filter order array to only include keys that exist in availableKeys (case-insensitive)
                const availableKeysLower = availableKeys.map(k => k.toLowerCase());
                const orderedKeys = mapping.order.filter(key => 
                    availableKeysLower.includes(key.toLowerCase())
                );
                
                // Match the case from availableKeys
                return orderedKeys.map(orderedKey => {
                    const matchingKey = availableKeys.find(
                        availKey => availKey.toLowerCase() === orderedKey.toLowerCase()
                    );
                    return matchingKey || orderedKey;
                });
            })
        );
    }

    /**
     * Check if a field name is already a formatted label (contains spaces, slashes, etc.)
     */
    private isAlreadyLabel(fieldName: string): boolean {
        return /[\s\/\-]/.test(fieldName);
    }

    /**
     * Get content key for a display label synchronously if mappings are already loaded
     * Used to map display field names (from fields_to_update) to actual content object keys
     * Performs case-insensitive matching against the content object keys
     * @param schemaKey - The WellView schema key
     * @param displayLabel - The display label (e.g., "Surface Location")
     * @param contentObject - Optional content object to match against for case-insensitive lookup
     * @returns The actual content key as it exists in the content object, or null if not found
     */
    public getContentKeyForLabel(schemaKey: string, displayLabel: string, contentObject?: any): string | null {
        if (!this.mappingsCache || !displayLabel) {
            return null;
        }
        
        const mapping = this.mappingsCache[schemaKey];
        if (!mapping || !mapping.fields) {
            return null;
        }
        
        // Find field by matching the display label (case-insensitive)
        const field = mapping.fields.find(f => 
            f.label && f.label.toLowerCase() === displayLabel.toLowerCase()
        );
        
        if (!field) {
            return null;
        }
        
        // Get the content key (stored in mapping)
        const mappedKey = field.contentKey || field.wvField;
        
        // If contentObject is provided, do case-insensitive lookup to find actual key
        if (contentObject) {
            const contentKeys = Object.keys(contentObject);
            const actualKey = contentKeys.find(key => 
                key.toLowerCase() === mappedKey.toLowerCase()
            );
            return actualKey || mappedKey;
        }
        
        return mappedKey;
    }

    /**
     * Get field label synchronously if mappings are already loaded
     * First tries to match by wvField, then by label (for API field names)
     * @param schemaKey - The WellView schema key
     * @param fieldName - The field name (either wvField or API field/label)
     */
    public getFieldLabelSync(schemaKey: string, fieldName: string): string {
        // If the field name is already a formatted label (has spaces, slashes, etc.), return it as-is
        if (this.isAlreadyLabel(fieldName)) {
            return fieldName;
        }
        
        if (!this.mappingsCache) {
            return this.formatFieldName(fieldName);
        }
        
        const mapping = this.mappingsCache[schemaKey];
        if (!mapping || !mapping.fields) {
            return this.formatFieldName(fieldName);
        }
        
        // First try to match by wvField (for backward compatibility)
        let field = mapping.fields.find(f => 
            f.wvField.toLowerCase() === fieldName.toLowerCase()
        );
        
        if (field) {
            return field.label;
        }
        
        // If not found, try to match by label (for API field names)
        field = mapping.fields.find(f => 
            f.label && f.label.toLowerCase() === fieldName.toLowerCase()
        );
        
        if (field) {
            return field.label;
        }
        
        // Fallback to formatting the field name
        return this.formatFieldName(fieldName);
    }

    /**
     * Get ordered display keys synchronously if mappings are already loaded
     * @param schemaKey - The WellView schema key
     * @param availableKeys - Keys available in the data
     */
    public getOrderedDisplayKeysSync(schemaKey: string, availableKeys: string[]): string[] {
        // If keys are already labels (contain spaces), return them as-is
        const hasLabelKeys = availableKeys.some(key => this.isAlreadyLabel(key));
        if (hasLabelKeys) {
            // These are already display labels from current data API, return as-is
            return availableKeys;
        }
        
        if (!this.mappingsCache) {
            return availableKeys;
        }
        
        const mapping = this.mappingsCache[schemaKey];
        if (!mapping || !mapping.order) {
            return availableKeys;
        }
        
        // Filter order array to only include keys that exist in availableKeys (case-insensitive)
        const availableKeysLower = availableKeys.map(k => k.toLowerCase());
        const orderedKeys = mapping.order.filter(key => 
            availableKeysLower.includes(key.toLowerCase())
        );
        
        // Match the case from availableKeys
        return orderedKeys.map(orderedKey => {
            const matchingKey = availableKeys.find(
                availKey => availKey.toLowerCase() === orderedKey.toLowerCase()
            );
            return matchingKey || orderedKey;
        });
    }

    /**
     * Format field name as fallback when no mapping exists
     */
    private formatFieldName(fieldName: string): string {
        if (!fieldName) return '';
        
        return fieldName
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
            .trim();
    }

    /**
     * Get field unit synchronously if mappings are already loaded
     * @param schemaKey - The WellView schema key
     * @param fieldName - The field name or label
     */
    public getFieldUnitSync(schemaKey: string, fieldName: string): string | undefined {
        if (!this.mappingsCache) {
            console.warn('[getFieldUnitSync] Mappings not loaded yet. Call getMappings() first.');
            return undefined;
        }
        
        const mapping = this.mappingsCache[schemaKey];
        if (!mapping || !mapping.fields) {
            return undefined;
        }
        
        // Try to find by wvField first (e.g., "Sz", "DepthTop")
        let field = mapping.fields.find(f => 
            f.wvField.toLowerCase() === fieldName.toLowerCase()
        );
        
        // If not found by wvField, try to find by label (e.g., "Holesize")
        if (!field) {
            field = mapping.fields.find(f => 
                f.label.toLowerCase() === fieldName.toLowerCase()
            );
        }
        
        const unit = field?.unitRBU;
        if (unit) {
            // console.log(`[Unit] ${fieldName} → ${unit}`);
        }
        return unit;
    }

    /**
     * Map API schema name to WellView schema name
     * @param apiSchemaName - The API schema name (e.g., 'Cas', 'WellboreSz')
     */
    public getWellViewSchemaName(apiSchemaName: string): string {
        const SCHEMA_TO_WELLVIEW_MAP: { [key: string]: string } = {
            'wellheader': 'wvWellHeader',
            'well_header': 'wvWellHeader',
            'Wellbore': 'wvWellbore',
            'wellbore': 'wvWellbore',
            'WellboreSz': 'wvWellboreSize',
            'wellbore_sz': 'wvWellboreSize',
            'wellbore_section': 'wvWellboreSize',
            'WellboreFormation': 'wvWellboreFormation',
            'geo_horizon': 'wvWellboreFormation',
            'geological-horizon': 'wvWellboreFormation',
            'Job': 'wvJob',
            'job': 'wvJob',
            'jobs': 'wvJob',
            'Cas': 'wvCas',
            'cas': 'wvCas',
            'casing': 'wvCas',
            'CasComp': 'wvCasComp',
            'cascomp': 'wvCasComp',
            'casing_component': 'wvCasComp',
            'Cement': 'wvCement',
            'cement': 'wvCement',
            'CementStage': 'wvCementStage',
            'cementstage': 'wvCementStage',
            'cement_stage': 'wvCementStage',
            'Perforation': 'wvPerforation',
            'perforation': 'wvPerforation',
            'OtherInHole': 'wvOtherInHole',
            'otherinhole': 'wvOtherInHole',
            'other_in_hole': 'wvOtherInHole',
            'Tubing': 'wvTubing',
            'tubing': 'wvTubing',
            'TubingComp': 'wvTubingComp',
            'tubing_comp': 'wvTubingComp',
            'Rod': 'wvRod',
            'rod': 'wvRod',
            'rod_details': 'wvRod',
            'RodComp': 'wvRodComp',
            'rod_comp': 'wvRodComp'
        };

        return SCHEMA_TO_WELLVIEW_MAP[apiSchemaName] || apiSchemaName;
    }
}
